import {
  LenderProfile as LenderProfileApi,
  AdminRole,
  VendorProfileOcaAutofillSettings,
} from '@trnsact/trnsact-shared-types';
import { Nullable } from 'global';

export type UserProfile = {
  id: string;
  email: string | null;
  dynamicsContactId: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  phoneNumber: string | null;
  mobilePhoneNumber: string | null;
  mugshot: string | null;
  title: string | null;
  accountId: string | null;
  status: string;
  vendorContactRole: VendorContactRoles;
  adminRole: AdminRole;
};

export type Account = {
  id: string;
  dynamicsAccountId: string | null;
  vendorProfileId: string | null;
  primaryContactId: string | null;
  name: string | null;
  logo: string | null;
  address: string | null;
  phone: string | null;
  primaryContactName: string | null;
  primaryContactPhone: string | null;
  primaryContactEmail: string | null;
  contacts: UserProfile[];
  magicEmailAddress: string | null;
};

export type LenderProfile = {
  id: string;
  hasActiveUser: boolean;
  lenderName: string;
  lenderLogo: string | null;
  showCustomApp: boolean;
  draftApplicationsEnabled: boolean;
  pdfTemplate: string;
  submissionMethod: string;
  lenderProfileId: string;
  lenderAccountId: string;
  lenderContactId: string | null;
  lenderChannelCode: number;
  lenderTypeCode: number;
  dealerRelationship: string | null;
  submissionReviewShowMappingModal: string;
  insuranceRequirements: string | null;
  lenderUserProfile: UserProfile | null;
  lenderAccount: Account | null;
};

export enum PortalConfigurationRolesSettingsKeys {
  Masked = 'masked',
  Hidden = 'hidden',
  ReadOnly = 'readOnly',
  HiddenOnPDF = 'hiddenOnPDF',
  ReportsHidden = 'reportsHidden',
  AssignedOnly = 'assignedOnly',
  LocationBased = 'locationBased',
  DocumentsByTypeSettingsHidden = 'documentsByTypeSettingsHidden',
  NotificationsManagementHidden = 'notificationsManagementHidden',
  RecertificationManagementHidden = 'recertificationManagementHidden',
  InternalCreditTiersManagementHidden = 'internalCreditTiersManagementHidden',
  LenderPortalSetupPageHidde = 'lenderPortalSetupPageHidde',
  CadmReadOnly = 'cadmReadOnly',
  DisallowVoСreation = 'disallowVoСreation',
  // Prescreen
  PrescreenType = 'prescreenType',
  HiddenPrescreenPage = 'hiddenPrescreenPage',
  HiddenPrescreenResults = 'hiddenPrescreenResults',
  HiddenPrescreenSubmissionAccess = 'hiddenPrescreenSubmissionAccess',
  HiddenPrescreenScorecardBuilder = 'hiddenPrescreenScorecardBuilder',
}

export type RouteConfig = {
  id: string;
  showInSidebarNav: boolean;
  exact: boolean;
  path: string; // always used in path for <Route /> component
  dynamicPath?: string; // used for dynamic path on sidebar nav item
  name: string;
  component: any /* ReactNode */;
  icon?: any /* ReactNode */;
  type?: string;
  supportsAnchor?: boolean;
  fastTrackOption?: boolean;
  isNewFeature?: boolean;
  isLegacyFeature?: boolean;
  action?: Function;
  isVisible: Function;
};

export interface ProposalProductsDialogModalData {
  products: any;
  selectedProposalsProductsIds: string[];
  onConfirm: (selectedProducts: any) => void;
}

export interface ReferenceTaskCompletionDataItem {
  fieldKey: string;
  value: string;
  label: string;
}

export interface ReferenceTaskCompletionData {
  taskId: string;
  data: ReferenceTaskCompletionDataItem[];
}

export interface VendorProfileDisclosures {
  dcrDisclosureLanguage?: string;
  dcrDisclosureLanguageSpanish?: string;
  dcrDisclosureTerms?: string;
  dcrDisclosureTermsSpanish?: string;
  ocaPersonalGuaranteeAgreementDisclosure?: string;
  proposalAcceptanceTermsDisclosure?: string;
  proposalDisclosure?: string;
  dcrMarketingDisclosure?: string;
  ocaAutofillSettings?: Nullable<VendorProfileOcaAutofillSettings>;
}

export interface DisclosuresCardData extends VendorProfileDisclosures {
  dcrDisclosureUseDefaultTerms: boolean;
}

export enum SsnStringPatternsByCountryCodes {
  US = '999-99-9999',
  CANADA = '999-999-999',
}

export enum LenderAssignmentTitles {
  RelationshipManager = 'Relationship Manager',
  Underwriter = 'Underwriter',
  SyndicationManager = 'Syndication Manager',
  CreditManager = 'Credit Manager',
}

export interface LenderProfilesWithOcaAccessVariables {
  accountId: string;
}

export interface LenderProfilesWithOcaAccessResponse {
  lenderProfilesWithOcaAccess: {
    data: LenderProfileApi[];
    success: boolean;
  };
}

export interface ImageGalleryItem {
  id: string;
  url: string;
}

export enum VendorContactRoles {
  SalesRep = 'sales_rep',
  SalesMgr = 'sales_mgr',
  ItAdmin = 'it_admin',
  Executive = 'executive',
  CreditMgr = 'credit_mgr',
  FinanceMgr = 'finance_mgr',
  SupportPerson = 'support_person',
}
